import axios from 'axios'

import useBrandAppConfig from './useBrandAppConfig'
import useDirtySnapshot from './useDirtySnapshot'
import {
  homeFieldsPayload,
  homeFromApi,
  menuPayload,
  newsPayload,
  offersPayload,
  type WebappHomeFields,
  type WebappMenu,
  type WebappNews,
  type WebappOffer
} from '../lib/brandAppHomeApi'

export default function useBrandAppHome() {
  const { home, order, updateOrder } = useBrandAppConfig()
  const offerServerIds = new Map<number, number>()

  const offersState = () => offersPayload(home.value, new Map())
  const newsState = () => newsPayload(home.value)
  const menuState = () => menuPayload(home.value, order.value.catalogUrl)

  const fieldsSnapshot = useDirtySnapshot(() => homeFieldsPayload(home.value))
  const offersSnapshot = useDirtySnapshot(offersState)
  const newsSnapshot = useDirtySnapshot(newsState)
  const menuSnapshot = useDirtySnapshot(menuState)
  const snapshots = [fieldsSnapshot, offersSnapshot, newsSnapshot, menuSnapshot]

  const rememberOfferIds = (offers: WebappOffer[]) => {
    offerServerIds.clear()
    offers.forEach((offer) => offerServerIds.set(offer.offerId, offer.id))
  }

  const load = async (webappId: number, fields: WebappHomeFields) => {
    const [offers, news, menu] = await Promise.all([
      axios.get<{ data: WebappOffer[] }>(`/webapps/${webappId}/offers`),
      axios.get<{ data: WebappNews[] }>(`/webapps/${webappId}/news`),
      axios.get<{ data: WebappMenu | null }>(`/webapps/${webappId}/menu`)
    ])

    rememberOfferIds(offers.data.data)
    home.value = homeFromApi(
      { ...fields, offers: offers.data.data, news: news.data.data, menu: menu.data.data },
      home.value
    )
    updateOrder('catalogUrl', menu.data.data?.url ?? '')
  }

  const fieldsPayload = () => homeFieldsPayload(home.value)

  const save = async (webappId: number) => {
    if (offersSnapshot.isDirty.value) {
      const { data } = await axios.put<{ data: WebappOffer[] }>(`/webapps/${webappId}/offers`, {
        offers: offersPayload(home.value, offerServerIds)
      })
      rememberOfferIds(data.data)
      offersSnapshot.capture()
    }
    if (newsSnapshot.isDirty.value) {
      await axios.put(`/webapps/${webappId}/news`, { news: newsState() })
      newsSnapshot.capture()
    }
    if (menuSnapshot.isDirty.value) {
      await axios.put(`/webapps/${webappId}/menu`, menuState())
      menuSnapshot.capture()
    }
  }

  return {
    load,
    save,
    fieldsPayload,
    areFieldsDirty: fieldsSnapshot.isDirty,
    captureFields: () => fieldsSnapshot.capture(),
    isDirty: computed(() => snapshots.some((snapshot) => snapshot.isDirty.value)),
    capture: () => snapshots.forEach((snapshot) => snapshot.capture())
  }
}
